import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { StaffAuthProvider, useStaffAuth } from '@/auth/StaffAuthProvider';
import { AdminShell } from '@/components/AdminShell';
import { LoadingState } from '@/components/AsyncState';

const LoginPage = lazy(async () => import('@/features/auth/LoginPage').then((module) => ({ default: module.LoginPage })));
const ClientProfilePage = lazy(async () => import('@/features/clients/ClientProfilePage').then((module) => ({ default: module.ClientProfilePage })));
const ClientsPage = lazy(async () => import('@/features/clients/ClientsPage').then((module) => ({ default: module.ClientsPage })));
const CollectionsPage = lazy(async () => import('@/features/collections/CollectionsPage').then((module) => ({ default: module.CollectionsPage })));
const InvoiceWorklistPage = lazy(async () => import('@/features/invoices/InvoiceWorklistPage').then((module) => ({ default: module.InvoiceWorklistPage })));
const PaymentReviewPage = lazy(async () => import('@/features/payments/PaymentReviewPage').then((module) => ({ default: module.PaymentReviewPage })));
const ApplicationsPage = lazy(async () => import('@/features/operations/OperationsPages').then((module) => ({ default: module.ApplicationsPage })));
const SupportPage = lazy(async () => import('@/features/operations/OperationsPages').then((module) => ({ default: module.SupportPage })));
const MessagingPage = lazy(async () => import('@/features/operations/OperationsPages').then((module) => ({ default: module.MessagingPage })));
const ReportsPage = lazy(async () => import('@/features/operations/OperationsPages').then((module) => ({ default: module.ReportsPage })));
const ExpensesPage = lazy(async () => import('@/features/operations/OperationsPages').then((module) => ({ default: module.ExpensesPage })));
const ContentPage = lazy(async () => import('@/features/operations/OperationsPages').then((module) => ({ default: module.ContentPage })));
const SystemHealthPage = lazy(async () => import('@/features/operations/OperationsPages').then((module) => ({ default: module.SystemHealthPage })));
const StagingTestingPage = lazy(async () => import('@/features/operations/OperationsPages').then((module) => ({ default: module.StagingTestingPage })));

function ProtectedLayout() {
  const { loading, signOut, stage, staff } = useStaffAuth();
  const location = useLocation();

  if (loading) return <LoadingState label="Checking staff access" />;
  if (!staff || stage !== 'ready') {
    return <Navigate replace state={{ from: location }} to="/login" />;
  }

  return (
    <AdminShell onSignOut={signOut} staff={staff}>
      <Outlet />
    </AdminShell>
  );
}

function LegacyStaffRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const path = location.pathname;
  let target = '/collections';

  if (path.startsWith('/staff/account/')) target = `/clients/${path.slice('/staff/account/'.length)}`;
  else if (path.startsWith('/staff/invoice/')) target = `/unpaid?q=${encodeURIComponent(path.slice('/staff/invoice/'.length))}`;
  else if (path === '/staff/accounts') target = '/clients';
  else if (path === '/staff/review') target = '/payment-review';
  else if (path === '/staff/invoices') {
    target = params.get('filter') === 'paid' ? '/paid' : '/unpaid';
  } else if (path === '/staff/applications') target = '/applications';
  else if (path === '/staff/tickets') target = '/support';
  else if (path === '/staff/messaging') target = '/messaging';
  else if (path.startsWith('/staff/reports')) target = '/reports';
  else if (path === '/staff/expenses') target = '/expenses';
  else if (path === '/staff/generate') target = '/system-health#manual-billing';
  else if (path === '/staff/content') target = '/content';
  else if (path === '/staff/health') target = '/system-health';
  else if (path === '/staff/testing') target = '/testing';

  const [targetPath, targetHash] = target.split('#', 2);
  const search = location.search
    ? targetPath.includes('?')
      ? `&${location.search.slice(1)}`
      : location.search
    : '';
  return <Navigate replace to={`${targetPath}${search}${targetHash ? `#${targetHash}` : ''}`} />;
}

export function App() {
  return (
    <StaffAuthProvider>
      <Suspense fallback={<LoadingState label="Opening workspace" />}>
        <Routes>
        <Route element={<LoginPage />} path="/login" />
        <Route element={<LegacyStaffRedirect />} path="/staff/*" />
        <Route element={<ProtectedLayout />}>
          <Route element={<CollectionsPage />} path="/collections" />
          <Route element={<ClientsPage />} path="/clients" />
          <Route element={<ClientProfilePage />} path="/clients/:accountId" />
          <Route element={<ClientsPage historyMode />} path="/history" />
          <Route element={<InvoiceWorklistPage mode="unpaid" />} path="/unpaid" />
          <Route element={<InvoiceWorklistPage mode="paid" />} path="/paid" />
          <Route element={<PaymentReviewPage />} path="/payment-review" />
          <Route element={<ApplicationsPage />} path="/applications" />
          <Route element={<SupportPage />} path="/support" />
          <Route element={<MessagingPage />} path="/messaging" />
          <Route element={<ReportsPage />} path="/reports" />
          <Route element={<ExpensesPage />} path="/expenses" />
          <Route element={<ContentPage />} path="/content" />
          <Route element={<SystemHealthPage />} path="/system-health" />
          <Route element={<StagingTestingPage />} path="/testing" />
          <Route element={<Navigate replace to="/collections" />} path="/" />
        </Route>
        <Route element={<Navigate replace to="/collections" />} path="*" />
        </Routes>
      </Suspense>
    </StaffAuthProvider>
  );
}
